import { useState, useEffect, useCallback } from 'react';

interface VulnPath {
  chain: string[]; cveId: string; cveSummary: string; severity: string;
  cvssScore?: number; fixedInVersion?: string; pathLength: number;
}

const SEV_COLORS: Record<string, string> = {
  critical: '#ef4444', high: '#f97316', medium: '#eab308', low: '#3b82f6', unknown: '#6b7280',
};
const SEV_BG: Record<string, string> = {
  critical: '#ef444420', high: '#f9731620', medium: '#eab30820', low: '#3b82f620', unknown: '#6b728020',
};
const SEV_ORDER: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1, unknown: 0 };

interface FixRecommendation {
  originalContent: string;
  fixedContent: string;
  changes: string[];
  packageManager: string;
}

export function VulnerabilityPanel({ projectName }: { projectName: string }) {
  const [vulns,     setVulns]     = useState<VulnPath[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [refreshing,setRefreshing]= useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [expanded,  setExpanded]  = useState<string | null>(null);
  const [filter,    setFilter]    = useState('all');

  // Fix Advisor State
  const [viewMode,  setViewMode]  = useState<'list' | 'fix'>('list');
  const [recommendation, setRecommendation] = useState<FixRecommendation | null>(null);
  const [loadingFix, setLoadingFix] = useState(false);
  const [applyingFix, setApplyingFix] = useState(false);
  const [fixSuccess, setFixSuccess] = useState<string | null>(null);
  const [fixError,   setFixError]   = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`/api/vulnerabilities/${encodeURIComponent(projectName)}`);
      if (!res.ok) throw new Error(`API ${res.status}`);
      const data = (await res.json()) as VulnPath[];
      setVulns(data.sort((a, b) => (SEV_ORDER[b.severity] ?? 0) - (SEV_ORDER[a.severity] ?? 0)));
    } catch (e) { setError(String(e)); }
    finally    { setLoading(false); }
  }, [projectName]);

  useEffect(() => {
    setViewMode('list');
    setRecommendation(null);
    setFixSuccess(null);
    setFixError(null);
    void load();
  }, [load]);

  const fetchRecommendation = async () => {
    setLoadingFix(true);
    setFixError(null);
    setFixSuccess(null);
    try {
      const res = await fetch(`/api/recommend-fix/${encodeURIComponent(projectName)}`);
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error ?? `API ${res.status}`);
      }
      const data = await res.json();
      setRecommendation(data);
    } catch (e) {
      setFixError(String(e));
    } finally {
      setLoadingFix(false);
    }
  };

  const handleApplyFix = async () => {
    if (!recommendation) return;
    setApplyingFix(true);
    setFixError(null);
    setFixSuccess(null);
    try {
      const res = await fetch(`/api/apply-fix/${encodeURIComponent(projectName)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fixedContent: recommendation.fixedContent }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error ?? `API ${res.status}`);
      }
      setFixSuccess('✅ package.json updated and dependency graph re-scanned! Vulnerability data has been refreshed automatically.');
      setRecommendation(null);
      await load();
    } catch (e) {
      setFixError(String(e));
    } finally {
      setApplyingFix(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true); setError(null);
    try {
      const res = await fetch(`/api/vuln-refresh/${encodeURIComponent(projectName)}`, { method: 'POST' });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error ?? `API ${res.status}`); }
      setRecommendation(null);
      setFixSuccess(null);
      setFixError(null);
      await load();
    } catch (e) { setError(String(e)); }
    finally     { setRefreshing(false); }
  };

  if (loading) return <PanelLoading label="Checking vulnerabilities…" />;
  if (error)   return (
    <div>
      <PanelError message={error} />
      <button onClick={() => void handleRefresh()} style={refreshBtn(false)} disabled={refreshing}>
        {refreshing ? '⏳ Refreshing…' : '🔄 Retry'}
      </button>
    </div>
  );

  const counts = { critical: 0, high: 0, medium: 0, low: 0 };
  for (const v of vulns) if (v.severity in counts) (counts as any)[v.severity]++;

  // Detect "all unknown" situation — means enrichment hasn't been fixed yet
  const allUnknown = vulns.length > 0 && vulns.every((v) => v.severity === 'unknown');

  const filtered = filter === 'all' ? vulns : vulns.filter((v) => v.severity === filter);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        marginBottom: '1.5rem', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#e6edf3', margin: '0 0 4px' }}>
            🚨 Vulnerabilities
          </h2>
          <p style={{ fontSize: '0.78rem', color: '#4d6079', margin: 0 }}>
            CVE security advisories from OSV and GitHub Advisory Database
          </p>
        </div>
        <button onClick={() => void handleRefresh()} disabled={refreshing} style={refreshBtn(refreshing)}>
          {refreshing ? '⏳ Re-scanning…' : '🔄 Re-scan'}
        </button>
      </div>

      {/* Toggle View Mode */}
      <div style={{ display: 'flex', borderBottom: '1px solid #1e2738', marginBottom: '1.25rem', gap: '1rem' }}>
        <button
          onClick={() => setViewMode('list')}
          style={{
            background: 'none', border: 'none', padding: '10px 4px', cursor: 'pointer',
            fontSize: '0.88rem', fontWeight: 600,
            color: viewMode === 'list' ? '#3b82f6' : '#4d6079',
            borderBottom: `2px solid ${viewMode === 'list' ? '#3b82f6' : 'transparent'}`,
            marginBottom: -1, transition: 'all 0.15s'
          }}
        >
          🚨 Vulnerabilities List
        </button>
        <button
          onClick={() => { setViewMode('fix'); void fetchRecommendation(); }}
          style={{
            background: 'none', border: 'none', padding: '10px 4px', cursor: 'pointer',
            fontSize: '0.88rem', fontWeight: 600,
            color: viewMode === 'fix' ? '#3b82f6' : '#4d6079',
            borderBottom: `2px solid ${viewMode === 'fix' ? '#3b82f6' : 'transparent'}`,
            marginBottom: -1, transition: 'all 0.15s'
          }}
        >
          🛡️ Security Fix Advisor
        </button>
      </div>

      {viewMode === 'list' ? (
        <>
          {/* Stale-data warning */}
          {allUnknown && (
            <div style={{ background: '#eab30818', border: '1px solid #eab30830', borderRadius: 10,
              padding: '0.85rem 1rem', marginBottom: '1.25rem', display: 'flex',
              alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
              <div>
                <div style={{ fontWeight: 600, color: '#eab308', marginBottom: 3 }}>
                  ⚠️ Severity data is stale
                </div>
                <div style={{ fontSize: '0.78rem', color: '#4d6079' }}>
                  Vulnerabilities were scanned before severity parsing was fixed.
                  Click <strong style={{ color: '#eab308' }}>Re-scan</strong> to refresh.
                </div>
              </div>
              <button onClick={() => void handleRefresh()} disabled={refreshing} style={refreshBtn(refreshing)}>
                {refreshing ? '⏳' : '🔄 Fix Now'}
              </button>
            </div>
          )}

          {/* Severity summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '0.6rem',
            marginBottom: '1.5rem' }}>
            {(['critical','high','medium','low'] as const).map((sev) => {
              const c = counts[sev];
              const color = SEV_COLORS[sev];
              const active = filter === sev;
              return (
                <button key={sev} onClick={() => setFilter(active ? 'all' : sev)}
                  style={{
                    background: active ? SEV_BG[sev] : '#0d1420',
                    border: `1px solid ${c > 0 ? color + '66' : '#1e2738'}`,
                    borderTop: active ? `2px solid ${color}` : `2px solid ${c > 0 ? color + '55' : '#1e2738'}`,
                    borderRadius: 10, padding: '0.9rem 0.5rem', cursor: 'pointer',
                    textAlign: 'center', transition: 'all 0.15s',
                    boxShadow: active ? `0 0 14px ${color}33` : 'none',
                  }}>
                  <div style={{ fontSize: '1.75rem', fontWeight: 800,
                    color: c > 0 ? color : '#2d3d52' }}>{c}</div>
                  <div style={{ fontSize: '0.68rem', color: c > 0 ? color + 'aa' : '#2d3d52',
                    marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{sev}</div>
                </button>
              );
            })}
          </div>

          {vulns.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem 1rem',
              background: '#0d1420', borderRadius: 12, border: '1px solid #1e2738' }}>
              <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>✅</div>
              <div style={{ fontWeight: 600, color: '#22c55e' }}>No known vulnerabilities found</div>
              <div style={{ fontSize: '0.8rem', color: '#2d3d52', marginTop: 4 }}>
                All packages in <strong style={{ color: '#4d6079' }}>{projectName}</strong> look clean
              </div>
              <button onClick={() => void handleRefresh()} disabled={refreshing}
                style={{ ...refreshBtn(refreshing), marginTop: '1rem', fontSize: '0.8rem' }}>
                {refreshing ? '⏳ Scanning…' : '🔄 Run fresh scan'}
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
              {/* Filter pills */}
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
                {['all', 'critical', 'high', 'medium', 'low', 'unknown'].map((f) => {
                  const cnt = f === 'all' ? vulns.length
                    : vulns.filter((v) => v.severity === f).length;
                  if (f !== 'all' && cnt === 0) return null;
                  const color = f === 'all' ? '#6366f1' : SEV_COLORS[f] ?? '#888';
                  const active = filter === f;
                  return (
                    <button key={f} onClick={() => setFilter(f)}
                      style={{
                        background: active ? color + '20' : '#0d1420',
                        border: `1px solid ${active ? color : '#1e2738'}`,
                        color: active ? color : '#4d6079',
                        borderRadius: 20, padding: '4px 12px', fontSize: '0.77rem',
                        cursor: 'pointer', fontWeight: active ? 600 : 400, transition: 'all 0.12s',
                      }}>
                      {f === 'all' ? 'All' : f} ({cnt})
                    </button>
                  );
                })}
              </div>

              {filtered.map((v, i) => {
                const color = SEV_COLORS[v.severity] ?? '#888';
                const key   = `${v.cveId}-${i}`;
                const open  = expanded === key;
                const noSummary = !v.cveSummary || v.cveSummary === 'No summary available';
                return (
                  <div key={key} style={{
                    background: '#0d1420', border: `1px solid ${color}33`,
                    borderLeft: `3px solid ${color}`, borderRadius: 10, overflow: 'hidden',
                    boxShadow: open ? `0 4px 16px ${color}18` : 'none', transition: 'box-shadow 0.15s',
                  }}>
                    <button onClick={() => setExpanded(open ? null : key)}
                      style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                        padding: '0.875rem 1rem', display: 'flex', alignItems: 'flex-start',
                        gap: '0.75rem', textAlign: 'left' }}>

                      {/* Severity badge */}
                      <span style={{ background: SEV_BG[v.severity] ?? '#0', color,
                        borderRadius: 5, padding: '3px 9px', fontSize: '0.7rem', fontWeight: 700,
                        textTransform: 'uppercase', flexShrink: 0, letterSpacing: '0.04em',
                        border: `1px solid ${color}44` }}>
                        {v.severity}
                      </span>

                      {/* CVE ID + summary */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: '#e6edf3', fontSize: '0.875rem' }}>
                          {v.cveId}
                        </div>
                        <div style={{ fontSize: '0.78rem',
                          color: noSummary ? '#2d3d52' : '#4d6079',
                          fontStyle: noSummary ? 'italic' : 'normal',
                          marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {noSummary ? 'No description available' : v.cveSummary}
                        </div>
                      </div>

                      {/* CVSS score pill */}
                      {typeof v.cvssScore === 'number' && (
                        <span style={{ fontSize: '0.72rem', color, background: color + '18',
                          borderRadius: 4, padding: '2px 7px', fontWeight: 700, flexShrink: 0 }}>
                          {v.cvssScore.toFixed(1)}
                        </span>
                      )}
                      <span style={{ color: '#2d3d52', fontSize: '0.75rem', flexShrink: 0 }}>
                        {open ? '▲' : '▼'}
                      </span>
                    </button>

                    {open && (
                      <div style={{ padding: '0 1rem 0.9rem', borderTop: '1px solid #1e2738' }}>
                        {/* Full summary */}
                        {!noSummary && (
                          <p style={{ fontSize: '0.82rem', color: '#8b99ae', marginTop: '0.75rem',
                            marginBottom: '0.75rem', lineHeight: 1.55 }}>
                            {v.cveSummary}
                          </p>
                        )}

                        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap',
                          marginBottom: '0.75rem', paddingTop: noSummary ? '0.75rem' : 0 }}>
                          {typeof v.cvssScore === 'number' && (
                            <Chip label="CVSS Score" value={v.cvssScore.toFixed(1)} color="#eab308" />
                          )}
                          {!!v.fixedInVersion && (
                            <Chip label="Fixed in" value={`≥ ${v.fixedInVersion}`} color="#22c55e" />
                          )}
                          <Chip label="Depth" value={`${v.pathLength} hops`} color="#6366f1" />
                        </div>

                        {/* Dependency chain */}
                        <div style={{ fontSize: '0.72rem', color: '#3d4f6b', marginBottom: '0.4rem',
                          textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Dependency path
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                          {v.chain.map((pkg, idx) => (
                            <span key={idx} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ background: '#141e2e', border: '1px solid #1e2738',
                                borderRadius: 4, padding: '2px 7px', fontSize: '0.73rem', color: '#8b99ae' }}>
                                {pkg}
                              </span>
                              {idx < v.chain.length - 1 && (
                                <span style={{ color: '#2d3d52' }}>→</span>
                              )}
                            </span>
                          ))}
                        </div>

                        <a href={`https://osv.dev/vulnerability/${v.cveId}`} target="_blank"
                          rel="noopener noreferrer"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                            marginTop: '0.75rem', color: '#3b82f6', fontSize: '0.78rem',
                            textDecoration: 'none', fontWeight: 500 }}>
                          View on OSV.dev ↗
                        </a>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        /* Fix Advisor Panel */
        <div style={{ background: '#0d1420', border: '1px solid #1e2738', borderRadius: 12, padding: '1.5rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#e6edf3', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: 6, marginTop: 0 }}>
            🛡️ Security Fix Advisor
          </h3>
          <p style={{ fontSize: '0.8rem', color: '#4d6079', marginBottom: '1.25rem', marginTop: 0 }}>
            DepGraph analyzes vulnerability paths and suggests updates to package.json (such as direct dependency bumps or resolutions/overrides for transitive vulnerabilities).
          </p>

          {loadingFix && <PanelLoading label="Analyzing safe security fixes…" />}

          {fixError && <PanelError message={fixError} />}

          {fixSuccess && (
            <div style={{ padding: '1rem', color: '#2ea043', fontSize: '0.82rem', background: '#2ea04312', borderRadius: 8, border: '1px solid #2ea04330', marginBottom: '1.25rem', lineHeight: 1.5 }}>
              {fixSuccess}
            </div>
          )}

          {!loadingFix && recommendation && (
            <div>
              {recommendation.changes.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#8b949e' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🛡️</div>
                  <div style={{ fontWeight: 600 }}>No security fixes needed</div>
                  <p style={{ fontSize: '0.78rem', color: '#4d6079', marginTop: 4 }}>
                    Your dependencies and overrides do not have any known vulnerability paths with available fixes.
                  </p>
                </div>
              ) : (
                <div>
                  {/* Changes List */}
                  <div style={{ marginBottom: '1.25rem' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>
                      Recommended Changes ({recommendation.changes.length})
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '1.2rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                      {recommendation.changes.map((change, idx) => (
                        <li key={idx} style={{ fontSize: '0.8rem', color: '#c9d1d9', lineHeight: 1.4 }}>
                          {change}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => void handleApplyFix()}
                      disabled={applyingFix}
                      style={{
                        background: 'linear-gradient(135deg,#2ea043,#238636)',
                        border: '1px solid #30363d',
                        color: '#fff',
                        borderRadius: 8, padding: '8px 16px', fontSize: '0.8rem', fontWeight: 600,
                        cursor: applyingFix ? 'not-allowed' : 'pointer',
                        transition: 'all 0.15s',
                        boxShadow: '0 2px 8px rgba(35,134,54,0.3)',
                      }}
                    >
                      {applyingFix ? '⏳ Applying Fixes…' : '🛡️ Apply Fixes to package.json'}
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(recommendation.fixedContent).then(() => {
                          alert('Copied proposed package.json to clipboard!');
                        });
                      }}
                      style={{
                        background: '#161b22',
                        border: '1px solid #30363d',
                        color: '#c9d1d9',
                        borderRadius: 8, padding: '8px 16px', fontSize: '0.8rem', fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      📋 Copy Proposed package.json
                    </button>
                  </div>

                  {/* Proposed File Preview */}
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>
                      Proposed package.json ({recommendation.packageManager})
                    </div>
                    <pre style={{
                      background: '#070a0f', border: '1px solid #1e2738', borderRadius: 8,
                      padding: '1rem', overflowX: 'auto', fontSize: '0.78rem', color: '#a5d6ff',
                      maxHeight: '350px', margin: 0, fontFamily: 'monospace'
                    }}>
                      <code>{recommendation.fixedContent}</code>
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function refreshBtn(disabled: boolean): React.CSSProperties {
  return {
    background: disabled ? '#1a2233' : '#0d1420',
    border: `1px solid ${disabled ? '#1e2738' : '#3b82f666'}`,
    color: disabled ? '#3d4f6b' : '#3b82f6',
    borderRadius: 8, padding: '7px 14px', fontSize: '0.8rem', fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.15s',
    whiteSpace: 'nowrap' as const,
  };
}

function Chip({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center',
      background: color + '12', border: `1px solid ${color}30`,
      borderRadius: 6, padding: '3px 9px' }}>
      <span style={{ fontSize: '0.7rem', color: '#4d6079' }}>{label}</span>
      <span style={{ fontSize: '0.8rem', color, fontWeight: 600 }}>{value}</span>
    </div>
  );
}

export function PanelLoading({ label }: { label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: 220, color: '#3d4f6b', gap: '0.6rem', fontSize: '0.88rem' }}>
      <span>⏳</span> {label}
    </div>
  );
}
export function PanelError({ message }: { message: string }) {
  return (
    <div style={{ padding: '1.25rem', color: '#ef4444', fontSize: '0.83rem',
      background: '#ef444412', borderRadius: 8, border: '1px solid #ef444430',
      marginBottom: '0.75rem' }}>
      ⚠ {message}
    </div>
  );
}
